/* eslint-disable @typescript-eslint/no-explicit-any */

import { apiVisitor, ApiResponse } from '@/api/api';
import { JOB_BOARD_URLS } from '@/api/url';
import { Agency } from '@/types/Agency';
import { ApplicationQuestionAnswer } from '@/types/ApplicationQuestionAnswer';
import { CandidateWithJobContext } from '@/types/Candidate';
import { EmploymentType, WorkType } from '@/types/enums';
import { Job, JobBasicInfo } from '@/types/Job';
import { isAxiosError } from 'axios';

export interface GetJobBoardInfoResponse {
  agency: Agency;
  categories: string[];
  work_types: WorkType[];
  employment_types: EmploymentType[];
  locations: string[];
}

export interface GetJobBoardInfoParams {
  agency_id: string;
}

export const apiGetJobBoardInfo = async ({
  params
}: {
  params: GetJobBoardInfoParams;
}): Promise<ApiResponse<GetJobBoardInfoResponse>> => {
  try {
    const response = await apiVisitor.get<ApiResponse<GetJobBoardInfoResponse>>(
      JOB_BOARD_URLS.GET_INFO(params.agency_id)
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetJobBoardJobsResponse {
  jobs: JobBasicInfo[];
  current_page: number;
  total_page: number;
}

export interface GetJobBoardJobsParams {
  page: number;
  limit: number;
  keyword?: string;
  category?: string;
  work_type?: WorkType;
  employment_type?: EmploymentType;
  location?: string;
}

export const apiGetJobBoardJobs = async ({
  agency_id,
  params
}: {
  agency_id: string;
  params: GetJobBoardJobsParams;
}): Promise<ApiResponse<GetJobBoardJobsResponse>> => {
  try {
    const response = await apiVisitor.get<ApiResponse<GetJobBoardJobsResponse>>(
      JOB_BOARD_URLS.GET_ALL(agency_id),
      { params }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface GetJobBoardJobParams {
  agency_id: string;
  job_id: string;
}

export interface GetJobBoardJobResponse {
  job: Job;
  agency: Agency;
}

export const apiGetJobBoardJob = async ({
  params
}: {
  params: GetJobBoardJobParams;
}): Promise<ApiResponse<GetJobBoardJobResponse>> => {
  try {
    const response = await apiVisitor.get<ApiResponse<GetJobBoardJobResponse>>(
      JOB_BOARD_URLS.GET_JOB({
        agency_id: params.agency_id,
        job_id: params.job_id
      })
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface ApplyJobPayload {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  linkedin: string;
  resume: File;
  application_answers?: ApplicationQuestionAnswer[];
}

export const apiApplyJob = async ({
  agencyId,
  jobId,
  payload
}: {
  agencyId: string;
  jobId: string;
  payload: ApplyJobPayload;
}): Promise<ApiResponse<CandidateWithJobContext>> => {
  try {
    const formData = new FormData();
    formData.append('first_name', payload.first_name);
    formData.append('last_name', payload.last_name);
    formData.append('email', payload.email);
    formData.append('phone', payload.phone);
    formData.append('linkedin', payload.linkedin);
    formData.append('resume', payload.resume);
    if (payload.application_answers) {
      formData.append('application_answers', JSON.stringify(payload.application_answers));
    }

    const response = await apiVisitor.post<ApiResponse<CandidateWithJobContext>>(
      JOB_BOARD_URLS.APPLY_JOB({
        agency_id: agencyId,
        job_id: jobId
      }),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export const apiApplyJobWithVeteransResume = async ({
  agencyId,
  jobId,
  payload
}: {
  agencyId: string;
  jobId: string;
  payload: ApplyJobPayload;
}): Promise<ApiResponse<CandidateWithJobContext>> => {
  try {
    const formData = new FormData();
    formData.append('first_name', payload.first_name);
    formData.append('last_name', payload.last_name);
    formData.append('email', payload.email);
    formData.append('phone', payload.phone);
    formData.append('linkedin', payload.linkedin);
    formData.append('file', payload.resume);
    if (payload.application_answers) {
      formData.append('application_answers', JSON.stringify(payload.application_answers));
    }

    const response = await apiVisitor.post<ApiResponse<CandidateWithJobContext>>(
      JOB_BOARD_URLS.APPLY_JOB_WITH_VETERANS_RESUME({
        agency_id: agencyId,
        job_id: jobId
      }),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};

export interface JobBoardTranslateVeteransResumePayload {
  resume: File;
}

export interface JobBoardTranslateVeteransResumeProgress {
  percentage: number;
  step: string;
}

export const apiJobBoardTranslateVeteransResume = async ({
  agencyId,
  payload,
  onProgress
}: {
  agencyId: string;
  payload: JobBoardTranslateVeteransResumePayload;
  onProgress?: (progress: JobBoardTranslateVeteransResumeProgress) => void;
}): Promise<ApiResponse<Resume>> => {
  const visitorToken: string = import.meta.env.VITE_API_VISITOR_TOKEN;

  const formData = new FormData();
  formData.append('file', payload.resume);

  const response = await fetch(JOB_BOARD_URLS.TRANSLATE_VETERANS_RESUME(agencyId), {
    method: 'POST',
    headers: {
      ...(visitorToken ? { Authorization: `Bearer ${visitorToken}` } : {})
    },
    body: formData
  });

  if (!response.ok || !response.body) {
    const errorData = await response
      .json()
      .catch(() => ({ code: response.status, message: response.statusText, data: null }));
    return errorData;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';
  let finalData: ApiResponse<Resume> | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          if (currentEvent === 'done') {
            finalData = parsed as ApiResponse<Resume>;
          } else {
            onProgress?.(parsed as JobBoardTranslateVeteransResumeProgress);
          }
        } catch {
          console.error('Failed to parse line:', line);
        }
        currentEvent = '';
      } else if (line === '') {
        currentEvent = '';
      }
    }
  }

  if (!finalData) {
    return { code: 500, message: 'No response data received', data: null as any };
  }

  return finalData;
};

export interface CheckVeteransFeatureEnabledResponse {
  agency: Agency;
  enabled: boolean;
}

export const apiCheckVeteransFeatureEnabled = async ({
  agency_id
}: {
  agency_id: string;
}): Promise<ApiResponse<CheckVeteransFeatureEnabledResponse>> => {
  try {
    const response = await apiVisitor.get<ApiResponse<CheckVeteransFeatureEnabledResponse>>(
      JOB_BOARD_URLS.CHECK_VETERANS_FEATURE_ENABLED(agency_id)
    );

    return response.data;
  } catch (error: any) {
    if (isAxiosError(error)) {
      return error.response?.data;
    }

    throw error;
  }
};
